<template>
  <div>
    <q-card flat class="q-ma-md">
      <q-card-section class="q-pt-none row q-col-gutter-md">
        <!-- Primeira linha: filtros channel e campaign -->
        <div class="col-6">
          <q-select
            v-model="params.channelSelected"
            :options="params.channelsFiltered"
            :label="$t('channel')"
            option-label="name"
            option-value="id"
            clear-icon="mdi-close"
            clearable
            use-input
            outlined
            input-debounce="0"
            @filter="filterChannels"
          />
        </div>
        <div class="col-6">
          <q-select
            v-model="params.campaignSelected"
            :options="params.campaignsFiltered"
            :label="$t('campaign')"
            option-label="name"
            option-value="id"
            clear-icon="mdi-close"
            clearable
            use-input
            outlined
            input-debounce="0"
            @filter="filterCampaigns"
          />
        </div>

        <!-- Segunda linha: novos filtros (affiliate manager, manager e data) -->
        <div class="col-4">
          <select-request
            :v-model="params.affiliate_manager_id"
            outlined
            endpoint="/user/affiliate/managers/redes"
            option-label="name"
            option-value="user_id"
            :label="$t('affiliate_manager')"
            clearable
            @update="(value) => (params.affiliate_manager_id = value === null ? 0 : value.user_id)"
          />
        </div>
        <div class="col-4">
          <select-request
            :v-model="params.manager_id"
            :endpoint="'/user/managers-unblocked'"
            :label="$t('manager')"
            outlined
            clearable
            option-label="name"
            option-value="user_id"
            @update="(val) => (params.manager_id = val === null ? null : val.user_id)"
          />
        </div>
        <div class="col-4">
          <date-input
            buttons
            outlined
            :label="$t('created_at')"
            :v-model="params.date"
            @update="(value) => (params.date = value)"
            range
            :rules="[(val) => !!val || $t('required_field')]"
          />
        </div>

        <!-- Terceira linha: botões -->
        <div class="col-12 text-right">
          <q-btn color="primary" :label="$t('clear')" no-caps outline class="q-px-md q-mx-md" @click="clearFilter()" />
          <q-btn
            color="positive"
            :label="$t('search')"
            no-caps
            unelevated
            class="q-px-md"
            @click="getChannelsPending()"
          />
        </div>
      </q-card-section>
    </q-card>

    <!-- Table section -->
    <div>
      <q-table
        :data="dataTable.data"
        :columns="columns"
        :row-key="generateRowKey"
        :selected.sync="itemSelect"
        selection="multiple"
        :no-data-label="$t('no_pending_channels')"
        :pagination="pagination"
      >
        <template v-slot:header-selection>
          <q-checkbox v-model="selectAll" @input="toggleSelectAll" :label="$t('select_all')" color="primary" />
        </template>

        <template v-slot:top>
          <q-toolbar>
            <q-btn
              unelevated
              :label="$t('approve')"
              round
              flat
              icon="mdi-thumb-up"
              color="positive"
              no-caps
              class="q-pl-sm q-pr-lg"
              @click="onApproveChannels(itemSelect)"
              :disabled="itemSelect.length == 0"
            >
              <q-tooltip content-class="bg-secondary text-white" content-style="font-size: 12px">
                {{ $t('approve_selected_channels') }}
              </q-tooltip>
            </q-btn>

            <q-btn
              unelevated
              :label="$t('repprove')"
              round
              flat
              icon="mdi-thumb-down-outline"
              color="negative"
              no-caps
              class="q-px-lg"
              @click="onRepproveChannels(itemSelect)"
              :disabled="itemSelect.length == 0"
            >
              <q-tooltip content-class="bg-secondary text-white" content-style="font-size: 12px">
                {{ $t('repprove_selected_channels') }}
              </q-tooltip>
            </q-btn>
          </q-toolbar>
        </template>

        <template v-slot:bottom>
          <q-toolbar class="bg-transparent" :class="!$q.dark.isActive ? 'text-dark' : 'text-white'">
            <div class="row items-center">
              <span class="q-mr-md">{{ $t('rows_per_page') }}</span>
              <q-select
                v-model="pagination.rowsPerPage"
                :options="[10, 20, 50, 100]"
                dense
                @input="onRowsPerPageChange"
                style="max-width: 60px; padding-left: 8px; padding-right: 8px"
              />
              <span>Total: {{ dataTable.total }}</span>
            </div>
            <q-space />
            <q-pagination
              unelevated
              v-model="currentPage"
              color="primary"
              :max="dataTable.last_page"
              :max-pages="6"
              boundary-numbers
              direction-links
            />
          </q-toolbar>
        </template>
      </q-table>
    </div>
  </div>
</template>

<script>
import DateInput from '../../widgets/inputsGlobal/DateInput.vue';
import SelectRequest from '@/widgets/inputsGlobal/SelectRequest.vue';

export default {
  components: { DateInput, SelectRequest },
  
  data() {
    return {
      channelsList: [],
      campaignsList: [],
      itemSelect: [],
      selectAll: false,
      params: {
        channelSelected: null,
        channelsFiltered: [],
        campaignSelected: null,
        campaignsFiltered: [],
        affiliate_manager_id: null,
        manager_id: null,
        date: null
      },
      dataTable: [],
      columns: [
        {
          name: 'site_id',
          label: this.$t('channel_id'),
          field: 'site_id',
          align: 'left',
          sortable: true
        },
        {
          name: 'site_name',
          label: this.$t('channel_name'),
          field: 'site_name',
          align: 'left',
          sortable: true
        },
        {
          name: 'vertical',
          label: 'Vertical',
          field: 'site_type',
          align: 'left'
        },
        {
          name: 'campaign_id',
          label: this.$t('campaign_id'),
          field: 'campaign_id',
          align: 'left',
          sortable: true
        },
        {
          name: 'campaign_name',
          label: this.$t('campaign_name'),
          field: 'campaign_name',
          align: 'left',
          sortable: true
        }
      ],
      pagination: {
        sortBy: 'site_id',
        descending: false,
        page: 1,
        rowsPerPage: localStorage.getItem('rowsPerPage') || 10,
        rowsNumber: 0
      }
    };
  },

  created() {
    this.getChannels();
    this.getCampaigns();
    
    // O select-request carrega os dados internamente através do endpoint
    // Portanto não precisamos das chamadas getManagersRedes() e getManagersOperational()
    
    const storedRowsPerPage = localStorage.getItem('rowsPerPage');
    if (storedRowsPerPage) {
      this.pagination.rowsPerPage = parseInt(storedRowsPerPage, 10);
    }
    this.getChannelsPending();
  },

  watch: {
    'dataTable.data': function () {
      this.selectAll = false;
    }
  },

  computed: {
    currentPage: {
      get() {
        return this.dataTable.current_page;
      },
      set(value) {
        if (value !== this.dataTable.current_page) this.getChannelsPending(value, this.pagination.rowsPerPage);
      }
    }
  },

  methods: {
    onApproveChannels(channels, alterStatus = 1) {
      this.$q
        .dialog({
          title: this.$t('attention') + '<em>!</em>',
          message: this.$t('approve_channels_confirmation'),
          cancel: {
            label: this.$t('cancel'),
            color: 'white',
            textColor: 'primary',
            flat: true
          },
          ok: { color: 'positive', label: this.$t('yes') },
          persistent: true,
          html: true
        })
        .onOk(async () => {
          this.onLoading(true);
          try {
            const { data, status } = await this.$http.post('/v2/redirect', {
              app: 'ADVERTISER',
              path: '/api/edit/campaign/channels_selecteds',
              params: {
                data: {
                  ids: channels.map((item) => item.campaign_site_id),
                  status: alterStatus
                }
              }
            });
            if (status === 200) {
              this.successNotify(data.message);
              this.dataTable.data = this.dataTable.data.filter(
                (item) => !channels.some((channel) => channel.campaign_site_id === item.campaign_site_id)
              );
              this.itemSelect = [];
              this.getChannelsPending();
            }
          } catch (error) {
            if (error.response && error.response.status === 500) this.errorNotify(this.$t('problem_try_again_later'));
            else this.errorNotify(error);
          } finally {
            this.onLoading(false);
          }
        });
    },

    onRepproveChannels(channels, alterStatus = 3) {
      this.$q
        .dialog({
          title: this.$t('attention') + '<em>!</em>',
          message: this.$t('reprove_channels_confirmation'),
          ok: { color: 'positive', label: this.$t('yes') },
          cancel: {
            label: this.$t('cancel'),
            color: 'white',
            textColor: 'primary',
            flat: true
          },
          persistent: true,
          html: true
        })
        .onOk(async () => {
          this.onLoading(true);
          try {
            const { data, status } = await this.$http.post('/v2/redirect', {
              app: 'ADVERTISER',
              path: '/api/edit/campaign/channels_selecteds',
              params: {
                data: {
                  ids: channels.map((item) => item.campaign_site_id),
                  status: alterStatus
                }
              }
            });
            if (status === 200) {
              this.successNotify(data.message);
              this.dataTable.data = this.dataTable.data.filter(
                (item) => !channels.some((channel) => channel.campaign_site_id === item.campaign_site_id)
              );
              this.itemSelect = [];
              this.getChannelsPending();
            }
          } catch (error) {
            if (error.response && error.response.status === 500) this.errorNotify(this.$t('problem_try_again_later'));
            else this.errorNotify(error);
          } finally {
            this.onLoading(false);
          }
        });
    },

    generateRowKey(row) {
      return row.campaign_site_id;
    },

    clearFilter() {
      this.params = {
        channelSelected: null,
        channelsFiltered: [],
        campaignSelected: null,
        campaignsFiltered: [],
        affiliate_manager_id: null,
        manager_id: null,
        date: null
      };
      this.getChannelsPending();
    },

    async getChannels() {
      this.onLoading(true);
      try {
        const { data, status } = await this.$http.get(`/redirect?app=AFFILIATE&path=/api/affiliate/site/filter`);
        if (status === 200) {
          this.channelsList = data.map((item) => ({
            id: item.id,
            name: `${item.name} - (id: ${item.id})`
          }));
        }
      } catch (error) {
        if (error.response && error.response.status === 500) {
          this.errorNotify(this.$t('problem_try_again_later'));
        } else {
          this.errorNotify(error);
        }
      } finally {
        this.onLoading(false);
      }
    },

    async getCampaigns() {
      this.onLoading(true);
      try {
        const { data, status } = await this.$http.get(`redirect?app=ADVERTISER&path=/api/get/campaign/all`);
        if (status === 200) {
          this.campaignsList = data.map((item) => ({
            id: item.id,
            name: `${item.name} - (id: ${item.id})`
          }));
        }
      } catch (error) {
        if (error.response && error.response.status === 500) {
          this.errorNotify(this.$t('problem_try_again_later'));
        } else {
          this.errorNotify(error);
        }
      } finally {
        this.onLoading(false);
      }
    },

    async getChannelsPending(page = 1, perPage = this.pagination.rowsPerPage) {
      this.onLoading(true);

      let query = `page=${page}%26per_page=${perPage}`;
      if (this.params.channelSelected) query += `%26channel_id=${this.params.channelSelected.id}`;
      if (this.params.campaignSelected) query += `%26campaign_id=${this.params.campaignSelected.id}`;
      if (this.params.affiliate_manager_id) query += `%26affiliate_manager_id=${this.params.affiliate_manager_id}`;
      if (this.params.manager_id) query += `%26manager_id=${this.params.manager_id}`;

      if (this.params.date) {
        let [from, , to] = this.params.date.split(' ');
        from = this.formatToEn(from);
        to = this.formatToEn(to);
        query += `%26start_date=${from}%26end_date=${to}`;
      }

      try {
        const { data, status } = await this.$http.get(
          `/redirect?app=ADVERTISER&path=/api/get/campaign/site/pending/list?${query}`
        );
        if (status === 200) this.dataTable = data;
      } catch (error) {
        console.error(error);
      } finally {
        this.onLoading(false);
      }
    },

    filterChannels(val, update) {
      update(() => {
        const needle = val.toLocaleLowerCase();
        if (!needle) {
          this.params.channelsFiltered = this.channelsList;
        } else {
          this.params.channelsFiltered = this.channelsList.filter(
            (v) => v.name.toLocaleLowerCase().indexOf(needle) > -1
          );
        }
      });
    },

    filterCampaigns(val, update) {
      update(() => {
        const needle = val.toLocaleLowerCase();
        if (!needle) {
          this.params.campaignsFiltered = this.campaignsList;
        } else {
          this.params.campaignsFiltered = this.campaignsList.filter(
            (v) => v.name.toLocaleLowerCase().indexOf(needle) > -1
          );
        }
      });
    },

    onRowsPerPageChange(value) {
      this.pagination.rowsPerPage = value;
      localStorage.setItem('rowsPerPage', value);
      this.getChannelsPending(1, value);
    },

    toggleSelectAll() {
      if (this.selectAll) {
        this.itemSelect = this.dataTable.data.slice();
      } else {
        this.itemSelect = [];
      }
    }
  }
};
</script>